
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { buscarCEP, CepResponse } from "@/services/cep";
import { useToast } from "@/components/ui/use-toast";
import { Search, Loader2 } from "lucide-react";

interface CepInputProps {
  onAddressFound: (address: CepResponse) => void;
  disabled?: boolean;
  defaultValue?: string;
}

export function CepInput({ onAddressFound, disabled, defaultValue = "" }: CepInputProps) {
  const [cep, setCep] = useState(defaultValue);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const handleCepChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    
    // Only allow numbers and limited to 9 chars (8 numbers + 1 hyphen)
    value = value.replace(/[^\d-]/g, "");
    
    // Format as 00000-000
    if (value.length > 5 && !value.includes("-")) {
      value = value.slice(0, 5) + "-" + value.slice(5, 8);
    }
    
    if (value.length <= 9) {
      setCep(value);
    }
  };

  const handleSearch = async () => {
    if (disabled) return;
    
    const cepClean = cep.replace(/\D/g, "");
    
    if (cepClean.length !== 8) {
      toast({
        variant: "destructive",
        title: "CEP inválido",
        description: "Por favor, insira um CEP válido com 8 dígitos.",
      });
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await buscarCEP(cepClean);
      onAddressFound(response);
      toast({
        title: "CEP encontrado",
        description: `Endereço encontrado em ${response.localidade}, ${response.uf}`,
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro ao buscar CEP",
        description: "Não foi possível encontrar o endereço. Verifique o CEP e tente novamente.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-2">
      <Label htmlFor="cep">CEP</Label>
      <div className="flex gap-2">
        <Input
          id="cep"
          placeholder="00000-000"
          value={cep}
          onChange={handleCepChange}
          disabled={disabled || isLoading}
          className="flex-1"
        />
        <Button 
          type="button" 
          onClick={handleSearch}
          disabled={disabled || isLoading || cep.replace(/\D/g, "").length !== 8}
        >
          {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        Digite seu CEP para buscar automaticamente seu endereço
      </p>
    </div>
  );
}
